import React from 'react';
import { Link } from 'react-router-dom';
import { Phone, Mail, Instagram } from 'lucide-react';
import { chatOnWhatsapp, openMail } from '../../utils/utils';
import { useTranslation } from 'react-i18next';
import { STUDENT_GUIDE_URL } from '../../constants/links';

export default function Footer() {
  const { t } = useTranslation();

  return (
    <footer className="bg-blue/60 pt-10">
      <div className="bg-white rounded-t-[50px] md:rounded-t-[80px] px-6 md:px-12 lg:px-20 pt-16 pb-10">
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-12 lg:gap-8">

            {/* Column 1: Brand & Slogan */}
            <div className="lg:col-span-1">
              <h2 className="text-lg md:text-2xl font-bold text-black-metal tracking-wider mb-6">
                EDUSTEPS2EUROPE
              </h2>
              <p className="font-playfair italic text-lg text-black-metal/80 leading-relaxed max-w-[280px]">
                {t('footer.tagline')}
              </p>
            </div>

            {/* Column 2: Navigation 1 */}
            <div className="flex flex-col gap-4">
              <Link to="/" className="text-black-metal/70 hover:text-orange transition-colors">
                {t('footer.nav_home')}
              </Link>
              <Link to="/services" className="text-black-metal/70 hover:text-orange transition-colors">
                {t('footer.nav_services')}
              </Link>
              <a
                target="_blank"
                rel="noopener noreferrer"
                href={STUDENT_GUIDE_URL}
                className="text-black-metal/70 hover:text-orange transition-colors">
                {t('footer.nav_guide')}
              </a>
            </div>

            {/* Column 3: Navigation 2 */}
            <div className="flex flex-col gap-4">
              <Link to="/contact" className="text-black-metal/70 hover:text-orange transition-colors">
                {t('footer.nav_contact')}
              </Link>
              <Link to="/faq" className="text-black-metal/70 hover:text-orange transition-colors">
                {t('footer.nav_faq')}
              </Link>
              <button
                type="button"
                onClick={() => chatOnWhatsapp(t('footer.wtsp'))}
                className="text-left text-black-metal/70 hover:text-orange transition-colors cursor-pointer"
              >
                {t('footer.nav_book')}
              </button>
            </div>

            {/* Column 4: Contact & Socials */}
            <div className="flex flex-col gap-6">
              <div className="space-y-3">
                <div onClick={() => chatOnWhatsapp()} className="flex items-center gap-3 text-black-metal/80 cursor-pointer">
                  <Phone size={18} className="text-black-metal" />
                  <span className="text-sm font-medium">+33 7 80 74 27 58</span>
                </div>
                <div onClick={openMail} className="flex items-center gap-3 text-black-metal/80 cursor-pointer">
                  <Mail size={18} className="text-black-metal" />
                  <span className="text-sm font-medium">contact@edusteps2europe.com</span>
                </div>
              </div>

              {/* Social Icons — replace the TODO hrefs with the real profile URLs */}
              <div className="flex items-center gap-4">
                <a href="https://www.tiktok.com/@edusteps2europe?_r=1&amp;_t=ZS-99IPo1TTFTB" target="_blank" rel="noopener noreferrer" aria-label="TikTok"
                  className="p-2 rounded-full hover:bg-orange/10 transition-colors text-black-metal">
                  <svg viewBox="0 0 24 24" width="22" height="22" fill="currentColor" aria-hidden="true">
                    <path d="M14.2 3h3.1a5.2 5.2 0 0 0 3.7 3.7v3.1a8.1 8.1 0 0 1-3.7-1v6.4a6.2 6.2 0 1 1-5.4-6.1v3.2a3.1 3.1 0 1 0 2.3 3V3Z" />
                  </svg>
                </a>
                <a href="https://www.instagram.com/edusteps2europe?igsi=NzN2OGVweDNkN2Z5" target="_blank" rel="noopener noreferrer" aria-label="Instagram"
                  className="p-2 rounded-full hover:bg-orange/10 transition-colors text-black-metal">
                  <Instagram size={22} aria-hidden="true" />
                </a>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Bottom Copyright Bar */}
      <div className="bg-light-orange py-4 px-6 md:px-12 lg:px-20">
        <div className="max-w-7xl mx-auto flex flex-col md:flex-row justify-between items-center gap-4 text-[13px] text-black-metal/80 font-medium">
          <p>{t('footer.copyright', { year: new Date().getFullYear() })}</p>
          <nav className="flex items-center gap-4" aria-label="Legal">
            <Link to="/terms" className="hover:text-black-metal hover:underline transition-colors">
              {t('footer.terms', 'Terms of Service')}
            </Link>
            <Link to="/privacy" className="hover:text-black-metal hover:underline transition-colors">
              {t('footer.privacy', 'Privacy Policy')}
            </Link>
            <Link to="/cgv" className="hover:text-black-metal hover:underline transition-colors">
              {t('footer.cgv', 'Terms of Sale')}
            </Link>
          </nav>
          <p className="flex items-center gap-1">
            {t('footer.designer')}
          </p>
        </div>
      </div>
    </footer>
  );
}
